import { css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Style } from "../Styles";
import { BaseElement } from "../BaseElement";
import { approxSpaceForTilesMb } from "../../MapTileCacher";
import { litIf } from "../LitUtils";
import { JobDescription, JobQueueInterface, JobType } from "../../JobQueueInterface";
import { JobProgressEvent } from "../../PersistentJobQueue";

type DownloadEntry = {
    source: JobQueueInterface;
    desc: JobDescription;
    inProgress: boolean;
    beingDeleted: boolean;
    deleteJobId: string;
};

@customElement("ds-map-cache-downloaded-control")
export class MapCacheDownloadedControl extends BaseElement {
    static override styles = [
        Style,
        css`
            .download {
                background-color: var(--sl-color-neutral-0);
                padding: 1em;
                border-radius: 0.5em;
            }

            p {
                margin-top: 0;
                margin-bottom: 0;
            }

            sl-button {
                margin-top: 0.5em;
                width: 100%;
            }

            sl-progress-bar {
                --height: 0.5em;
            }
        `,
    ];

    @state() protected _downloads: DownloadEntry[] = [];
    @state() protected _progress?: number;
    @state() protected _progressId?: string;

    private DOWNLOADERS = [this._kernel.tileDownloader, this._kernel.placeMediaDownloader];

    public override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        for (const d of this.DOWNLOADERS) {
            this._subscribeToKernelEvent(
                [d.JOB_COMPLETE_EVENT, d.JOB_DELETED_EVENT, d.JOB_STARTED_EVENT, d.JOB_ADDED_EVENT],
                async () => {
                    this._progress = undefined;
                    this._progressId = undefined;
                    await this._update();
                }
            );

            this._subscribeToKernelEvent(d.JOB_PROGRESS_EVENT, (e: JobProgressEvent) => {
                this._updateProgress(e);
            });
        }

        await this._update();
    }

    private _updateProgress(e: JobProgressEvent) {
        for (const d of this._downloads) {
            if ((d.deleteJobId === e.id || d.desc.id === e.id) && d.inProgress) {
                this._progress = (e.index / e.count) * 100;
                this._progressId = d.desc.id;
                break;
            }
        }
    }

    private async _update() {
        let downloads: DownloadEntry[] = [];
        for (const d of this.DOWNLOADERS) {
            {
                const completed = await d.completedJobs();
                let outstanding = await d.outstandingJobs();
                const deletes = outstanding.filter((x) => x.type === JobType.Delete);
                outstanding = outstanding.filter((x) => x.type === JobType.Sync);

                const c = completed.map((desc) => {
                    return {
                        source: d,
                        desc,
                        inProgress: false,
                        beingDeleted: false,
                        progress: 100,
                        deleteJobId: "",
                    };
                });

                const o = outstanding.map((desc) => {
                    return {
                        source: d,
                        desc,
                        inProgress: true,
                        beingDeleted: false,
                        progress: 0,
                        deleteJobId: "",
                    };
                });
                const all = [...o, ...c];
                for (const d of all) {
                    const deleteJob = deletes.filter((x) => x.userData.jobId === d.desc.id);
                    if (deleteJob.length > 0) {
                        d.beingDeleted = true;
                        d.inProgress = true;
                        d.deleteJobId = deleteJob[0].id;
                    }
                }
                downloads = [...all, ...downloads];
            }
        }
        this._downloads = downloads;
    }

    private async _deleteCache(download: DownloadEntry) {
        await download.source.deleteJob(download.desc.id);
    }

    override render() {
        return html`<div class="flex-column">
            ${litIf(
                this._downloads.length > 0,
                () =>
                    html`${this._downloads.map(
                        (x) =>
                            html`<div class="flex-column download">
                                <p>${x.desc.description}</p>
                                <div class="flex-row" style="justify-content: space-between">
                                    <p>${x.desc.date}</p>
                                    ${litIf(
                                        x.source.name === "tile",
                                        () => html`
                                            <p>
                                                ${x.desc.numTiles} tiles
                                                (${approxSpaceForTilesMb(x.desc.numTiles)}mb)
                                            </p>
                                        `
                                    )}
                                </div>
                                ${litIf(
                                    x.inProgress,
                                    () =>
                                        html`<div class="progress-row flex-column">
                                                ${litIf(
                                                    x.beingDeleted,
                                                    () => html`<span>Deleting</span>`,
                                                    () => html`<span>Downloading</span>`
                                                )}
                                                ${litIf(
                                                    this._progressId === x.desc.id,
                                                    () =>
                                                        html`<sl-progress-bar
                                                            value=${this._progress ?? 0}
                                                        ></sl-progress-bar>`,
                                                    () => html`<span>Waiting...</span>`
                                                )}
                                            </div>
                                            ${litIf(
                                                !x.beingDeleted,
                                                () =>
                                                    html` <sl-button
                                                        size="small"
                                                        @click=${() => this._deleteCache(x)}
                                                        >Cancel</sl-button
                                                    >`
                                            )} `,
                                    () =>
                                        html` <sl-button
                                            size="small"
                                            @click=${() => this._deleteCache(x)}
                                            >Delete</sl-button
                                        >`
                                )}
                            </div>`
                    )}`,
                () => html`<span>No downloads</span>`
            )}
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-map-cache-downloaded-control": MapCacheDownloadedControl;
    }
}
